import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { createApolloClient } from '@/apolloClient';
import { useAuth } from '@/helpers/authUtils';
import { useEnvContext } from '@/helpers/envUtils';
import { requiresReactivation } from '@/helpers/signinUtils';
import { attemptSocialLogin, attemptSocialSignUp } from '@/helpers/socialSigninAction';
import { removeStoredValue } from '@/helpers/storage';
import { beginApiCall, endApiCall } from '@/reducers/config';
import { SignInFormData } from '@/schemas/signInSchema';

export const useSignInLogic = (isFromIframe = false) => {
  const { BASE_URL } = useEnvContext();
  const Auth = useAuth();
  const dispatch = useDispatch();

  const [isShowReactivationFlow, setIsShowReactivationFlow] = useState(false);
  const [client, setClient] = useState<any>(null);
  const [claimBookingCredentials, setClaimBookingCredentials] = useState<SignInFormData>({
    email: '',
    password: '',
  });

  const handleSignIn = useCallback(
    async ({ email, password }: SignInFormData) => {
      try {
        dispatch(beginApiCall());

        const response = await Auth?.loginEmail({
          email: email?.trim(),
          password: password?.trim(),
        });

        if (requiresReactivation(response)) {
          const newClient = createApolloClient(BASE_URL, (response as { accessToken: string }).accessToken);
          setClient(newClient);
          removeStoredValue('Authorization');
          setIsShowReactivationFlow(true);
          // Store email and password for pre-fill after reactivation
          setClaimBookingCredentials({
            email: email?.trim(),
            password: password.trim(),
          });
          dispatch(endApiCall());
          return { requiresReactivation: true, client: newClient };
        }

        if (isFromIframe) {
          dispatch(endApiCall());
        }

        return { success: true, session: response };
      } catch (error) {
        dispatch(endApiCall());
        throw error;
      }
    },
    [Auth, BASE_URL, dispatch],
  );

  const handleSocialLogin = useCallback(
    async (formFieldValues: any) => {
      const response = await dispatch(attemptSocialLogin(formFieldValues, Auth) as any);

      if (response.accessToken) {
        if (requiresReactivation(response)) {
          const newClient = createApolloClient(BASE_URL, response?.accessToken);
          setClient(newClient);
          removeStoredValue('Authorization');
          setIsShowReactivationFlow(true);
          dispatch(endApiCall());
          return { requiresReactivation: true, client: newClient };
        }

        if (isFromIframe) {
          dispatch(endApiCall());
        }

        return { success: true, session: response };
      }

      if (response.status === 401) {
        return { requiresSignup: true };
      }

      throw new Error('Something went wrong');
    },
    [Auth, BASE_URL, dispatch],
  );

  const handleSocialSignup = useCallback(
    async (formFieldsState: any) => {
      const response = await dispatch(attemptSocialSignUp(formFieldsState, Auth, BASE_URL) as any);

      if (response?.accessToken) {
        return { success: true, session: response };
      }
      console.error('Social signup failed:', response);

      throw new Error('Something went wrong');
    },
    [Auth, BASE_URL, dispatch],
  );

  const exitReactivationFlow = useCallback(() => {
    // eslint-disable-next-line no-underscore-dangle -- until the SDK exposes a public clearAuth()
    Auth?._clearAuth?.();
    setClient(null);
    setIsShowReactivationFlow(false);
  }, [Auth]);

  return {
    handleSignIn,
    handleSocialLogin,
    handleSocialSignup,
    exitReactivationFlow,
    isShowReactivationFlow,
    client,
    claimBookingCredentials,
  };
};
